import { useMemo, useCallback, Fragment } from 'react';
import { Box } from '@mui/material';
import { AdvancedFilter } from '../advanced-filter/AdvancedFilter';
import { SingleSelectFilter } from '../single-select-filter/SingleSelectFilter';
import { FiltersPanel } from '../filters-panel/FiltersPanel';
import { FilterItem, FilterValues, FilterComponentType, Filters, FilterToggleItem } from './types';
import { ToggleFilter } from '../ToggleFilter';

export interface FiltersRendererProps {
  filters: Filters;
  filterValues: FilterValues;
  onFilterChange: (filterKey: string, values: string[]) => void;
  onClearAll?: () => void;
  withPanelWrapper?: boolean;
  gap?: number;
  highlightChangedFilter?: boolean;
  checkFiltersHaveValuesCb?: (
    filters: { [key: string]: FilterItem | FilterToggleItem },
    filterValues: FilterValues,
  ) => boolean;
  anchorRight?: boolean;
}

/**
 * This component is used to render either
 * AdvancedFilter/SingleSelectFilter/ToggleFilter
 * or
 * AdvancedFilterPanel/SingleSelectFilterPanel/ToggleFilterPanel
 * depending on the props
 */
export const FiltersRenderer = ({
  filters,
  filterValues,
  onFilterChange,
  onClearAll,
  withPanelWrapper = false,
  gap = 2,
  highlightChangedFilter,
  checkFiltersHaveValuesCb,
  anchorRight,
}: FiltersRendererProps) => {
  const renderFilter = useCallback(
    (filterKey: string, filter: FilterItem | FilterToggleItem) => {
      const selectedValues = filterValues[filterKey]?.selectedValues || [];

      switch (filter.type) {
        case FilterComponentType.MULTI_SELECT:
          if (!('items' in filter) || !filter.items?.length) {
            return <Fragment key={filterKey} />;
          }
          return (
            <AdvancedFilter
              key={filterKey}
              items={filter.items}
              selectedItems={selectedValues}
              onSelectedItemsChange={(items) => onFilterChange(filterKey, items)}
              texts={{ title: filter.label }}
              renderSeparateClearButton={withPanelWrapper}
              highlightChangedFilter={highlightChangedFilter}
            />
          );

        case FilterComponentType.SINGLE_SELECT:
          if (!('items' in filter) || !filter.items?.length) {
            return <Fragment key={filterKey} />;
          }

          return (
            <SingleSelectFilter
              key={filterKey}
              options={filter.items}
              selectedItemValue={selectedValues[0]}
              onSelectItem={(value) => onFilterChange(filterKey, [value])}
              filterTitleText={filter.label}
              renderSeparateClearButton={withPanelWrapper}
            />
          );

        case FilterComponentType.TOGGLE_FILTER:
          if (!('checked' in filter)) {
            return <Fragment key={filterKey} />;
          }
          return (
            <ToggleFilter
              key={filterKey}
              checked={selectedValues.length > 0 && selectedValues[0] === 'true'}
              label={filter.label}
              onChange={(checked) => onFilterChange(filterKey, [checked.toString()])}
            />
          );

        default:
          return null;
      }
    },
    [filterValues, onFilterChange, highlightChangedFilter, withPanelWrapper],
  );

  const filtersJsx = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap,
        }}
      >
        {Object.entries(filters).map(([filterKey, filter]) => renderFilter(filterKey, filter))}
      </Box>
    );
  }, [filters, renderFilter, gap]);

  const panelJsx = useMemo(() => {
    const showClearAll = checkFiltersHaveValuesCb
      ? checkFiltersHaveValuesCb(filters, filterValues)
      : Object.entries(filters).length > 0 &&
        Object.values(filterValues).some((filterValue) => filterValue?.selectedValues?.length > 0);

    const hasFilters =
      Object.keys(filters).length > 0 &&
      Object.values(filters).some((filter) => 'items' in filter && filter.items?.length > 0);

    if (!hasFilters) {
      return null;
    }

    return (
      <FiltersPanel
        displayClearAll={showClearAll}
        onClearAll={onClearAll}
        anchorRight={anchorRight}
      >
        {filtersJsx}
      </FiltersPanel>
    );
  }, [filtersJsx, onClearAll, filterValues, filters, checkFiltersHaveValuesCb, anchorRight]);

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {withPanelWrapper && panelJsx}
      {!withPanelWrapper && filtersJsx}
    </Box>
  );
};
